/**
 * Airdrop shapes.
 *
 * Merkle-based token distribution with various vesting patterns.
 */

import {
  AIRDROPS_EVM_INSTANT,
  AIRDROPS_EVM_LL,
  AIRDROPS_EVM_LT,
  AIRDROPS_EVM_VCA,
  AIRDROPS_SOLANA_INSTANT,
  AIRDROPS_V2_EVM_LL,
  AIRDROPS_V3_EVM_LL,
} from "./contracts.js";
import { Shape } from "./enums.js";
import type { AirdropShapesRecord } from "./types.js";
import { defineAirdropShape } from "./types.js";

/**
 * Instant airdrop.
 * Tokens are claimable immediately with no vesting.
 */
const instant = defineAirdropShape(Shape.Airdrops.Instant, {
  evm: AIRDROPS_EVM_INSTANT,
  hasPredictableGas: true,
  isDeprecated: false,
  name: "Instant",
  solana: AIRDROPS_SOLANA_INSTANT,
});

/**
 * Linear airdrop.
 * Claimed tokens vest linearly from start to end.
 */
const linear = defineAirdropShape(Shape.Airdrops.Linear, {
  evm: AIRDROPS_EVM_LL,
  hasPredictableGas: true,
  isDeprecated: false,
  name: "Linear",
});

/**
 * Cliff airdrop.
 * Claimed tokens vest with a cliff period followed by linear vesting.
 */
const cliff = defineAirdropShape(Shape.Airdrops.Cliff, {
  evm: AIRDROPS_EVM_LL,
  hasPredictableGas: true,
  isDeprecated: false,
  name: "Cliff",
});

/**
 * Linear with initial unlock, then linear vesting.
 * A percentage is immediately available, remainder vests linearly.
 */
const linearUnlockLinear = defineAirdropShape(Shape.Airdrops.LinearUnlockLinear, {
  evm: AIRDROPS_V2_EVM_LL,
  hasPredictableGas: true,
  isDeprecated: false,
  name: "Linear Unlock Linear",
});

/**
 * Linear with initial unlock, then cliff.
 * A percentage is immediately available, remainder vests after cliff.
 */
const linearUnlockCliff = defineAirdropShape(Shape.Airdrops.LinearUnlockCliff, {
  evm: AIRDROPS_V2_EVM_LL,
  hasPredictableGas: true,
  isDeprecated: false,
  name: "Linear Unlock Cliff",
});

/**
 * Step-based airdrop via linear granularity.
 * Claimed tokens unlock in discrete steps at regular intervals using MerkleLL with granularity.
 *
 * @remarks
 * Replaces {@link tranchedStepper} by leveraging the granularity feature introduced in Airdrops v3.0.
 */
const linearStepper = defineAirdropShape(Shape.Airdrops.LinearStepper, {
  evm: AIRDROPS_V3_EVM_LL,
  hasPredictableGas: true,
  isDeprecated: false,
  name: "Linear Stepper",
});

/**
 * Step-based airdrop via tranches.
 * Claimed tokens unlock in discrete steps.
 */
const tranchedStepper = defineAirdropShape(Shape.Airdrops.TranchedStepper, {
  evm: AIRDROPS_EVM_LT,
  hasPredictableGas: false,
  isDeprecated: true,
  name: "Tranched Stepper",
});

/**
 * Variable claim amount airdrop.
 * Each recipient can claim a different amount based on Merkle proof data.
 */
const variableClaimAmount = defineAirdropShape(Shape.Airdrops.VariableClaimAmount, {
  evm: AIRDROPS_EVM_VCA,
  hasPredictableGas: true,
  isDeprecated: false,
  name: "Variable Claim Amount",
});

/**
 * All airdrop shapes indexed by shape ID.
 * Keys are alphabetically ordered for consistency.
 */
export const airdropShapes = {
  [Shape.Airdrops.Cliff]: cliff,
  [Shape.Airdrops.Instant]: instant,
  [Shape.Airdrops.Linear]: linear,
  [Shape.Airdrops.LinearStepper]: linearStepper,
  [Shape.Airdrops.LinearUnlockCliff]: linearUnlockCliff,
  [Shape.Airdrops.LinearUnlockLinear]: linearUnlockLinear,
  [Shape.Airdrops.TranchedStepper]: tranchedStepper,
  [Shape.Airdrops.VariableClaimAmount]: variableClaimAmount,
} satisfies AirdropShapesRecord;
